import {
  CheckboxWrapper,
  ContentListItem,
  ContentRowRightWrapper,
  HitLabel,
  SearchResultsWrapper,
} from './styles';
import { PLAN_ENTERPRISE_PLUS_LAB, PLAN_LAB_EXPERIENCE } from 'constants/plans_config';
import {
  PreviewCollection,
  PreviewCourse,
  PreviewLab,
  PreviewVideo,
} from 'pages/Playlists/components/PreviewContent';
import { UseInfiniteHitsProps, useInfiniteHits } from 'react-instantsearch-hooks';
import { useContext, useEffect, useRef, useState } from 'react';

import { CONTENT_TYPES } from 'constants/playlists';
import { CONTENT_TYPES as CONTENT_TYPE_SLUGS } from 'constants/content/content';
import Checkbox from 'components/checkbox';
import ContentTypeTag from './ContentTypeTag';
import { IContentItem } from './AddContent';
import Icon from 'components/icons';
import Notification from 'components/notification';
import PreviewLearningPath from '../PreviewContent/PreviewLearningPath';
import { SessionContext } from 'contexts/session';
import { Spinner } from 'components/loadings';
import Tooltip from 'components/tooltip';
import algoliasearch from 'algoliasearch';
import useWindowSize from 'hooks/useWindowSize';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';

interface SearchResultsProps extends UseInfiniteHitsProps {
  contentToExclude: Record<string, IContentItem>;
  selectedContent: Record<string, IContentItem>;
  onCheckContent: (hit: IContentItem) => void;
}

const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID,
  process.env.REACT_APP_ALGOLIA_APP_KEY
);

const contentIndex = searchClient.initIndex(process.env.REACT_APP_ALGOLIA_SEARCH_INDEX);

const PreviewComponent = {
  course: PreviewCourse,
  video: PreviewVideo,
  lab: PreviewLab,
  collection: PreviewCollection,
  'learning-path': PreviewLearningPath,
};

// Rendered as an element, not called: these components use hooks, so invoking them
// directly runs those hooks in SearchResults' own slot and breaks hook order as
// previews load. An unregistered type renders nothing rather than crashing.
const PreviewFor = ({ contentType, data }: { contentType: string; data: unknown }) => {
  const Preview = PreviewComponent[contentType];
  return Preview ? <Preview data={data as never} /> : null;
};

export const SearchResults = ({
  contentToExclude,
  selectedContent,
  onCheckContent,
  ...props
}: SearchResultsProps): JSX.Element => {
  const { isScreenMd } = useWindowSize();
  const { hits, isLastPage, showMore } = useInfiniteHits(props);
  const collectionsEnabled = useBooleanFlag(
    FEATURE_FLAGS.SKILL_DIVE_COLLECTIONS_IN_PLAYLISTS,
    false
  );

  const filteredHits = hits.filter((hit) => {
    if (contentToExclude[hit.objectID] || hit.content_type === 'bootcamp') return false;
    if (hit.content_type === CONTENT_TYPE_SLUGS.COLLECTION) {
      return collectionsEnabled && !hit.excluded_from_playlists;
    }
    return true;
  });
  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const [hitsDetails, setHitsDetails] = useState<Record<string, any>>({});
  const [tooltipVisible, setTooltipVisible] = useState(null);
  const { account } = useContext(SessionContext);

  const has_lab_experience =
    account?.pricing_plan === PLAN_LAB_EXPERIENCE ||
    account?.pricing_plan === PLAN_ENTERPRISE_PLUS_LAB;

  const has_skill_dive =
    account?.account_available_addons?.filter((ad) => ad.name === CONTENT_TYPES.SKILL_DIVE).length >
    0;

  useEffect(() => {
    let hitsTooltip = {};
    filteredHits.map((hit) => {
      hitsTooltip = {
        ...hitsTooltip,
        [hit.objectID]: false,
      };
    });
  }, [filteredHits]);

  useEffect(() => {
    if (sentinelRef.current === null) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLastPage) showMore();
        });
      },
      // The list scrolls in its own container, so the viewport is the wrong root.
      // rootMargin fetches the next page just before the sentinel comes into view.
      { root: scrollContainerRef.current, rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
    // hits.length matters as well as filteredHits.length: a page whose hits are all
    // filtered out leaves the sentinel intersecting with no new notification, and
    // without re-arming here pagination stops on the last page that had a visible row.
  }, [isLastPage, showMore, hits.length, filteredHits.length]);

  useEffect(() => {
    if (hits.length && !filteredHits.length && !isLastPage) showMore();
  }, [hits.length, filteredHits.length, isLastPage, showMore]);

  const getHit = async (hit) => {
    if (hitsDetails[`${hit.content_type}:${hit.objectID}`]) return;

    try {
      const hitDetail = await contentIndex.getObject(hit.objectID);
      setHitsDetails((prevState) => ({
        ...prevState,
        [`${hit.content_type}:${hit.objectID}`]: hitDetail,
      }));
    } catch (error) {
      Notification({ text: 'Error loading details', type: 'error' });
    }
  };

  const handleVisibility = (value: boolean, hit: any) => {
    setTooltipVisible((prevState) => ({
      ...prevState,
      [hit.objectID]: value,
    }));
    value && getHit(hit);
  };

  return (
    <SearchResultsWrapper ref={scrollContainerRef}>
      {filteredHits.map((hit: any) => (
        <ContentListItem key={hit.objectID}>
          <CheckboxWrapper
            onClick={() =>
              hit.is_lab_experience && (!has_skill_dive || !has_lab_experience)
                ? null
                : onCheckContent(hit)
            }
          >
            <Checkbox
              style={{
                fontWeight: '500',
                fontSize: '14px',
                lineHeight: '20px',
              }}
              checked={!!selectedContent[hit.objectID]}
              onChange={() => onCheckContent(hit)}
              controlled
              disabled={hit.is_lab_experience && !has_lab_experience && !has_skill_dive}
            />
            <HitLabel disabled={hit.is_lab_experience && !has_skill_dive && !has_lab_experience}>
              {hit.name}
            </HitLabel>
          </CheckboxWrapper>
          <ContentRowRightWrapper>
            <ContentTypeTag
              value={hit.content_type}
              is_lab_experience={hit.is_lab_experience}
              is_trackable={hit.total_flags > 0 || !!hit.flags?.length}
              has_skill_check_labs={hit.has_skill_check_labs}
            />
            <Tooltip
              title={
                hitsDetails[`${hit.content_type}:${hit.objectID}`] ? (
                  <PreviewFor
                    contentType={hit.content_type}
                    data={hitsDetails[`${hit.content_type}:${hit.objectID}`]}
                  />
                ) : (
                  <Spinner active position="static" />
                )
              }
              placement={isScreenMd ? 'right' : 'bottomRight'}
              trigger={['hover', 'click']}
              zIndex={9999}
              overlayInnerStyle={{
                boxShadow: '0px 0px 48px rgba(0, 0, 0, 0.2)',
                borderRadius: '10px',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isScreenMd ? '500px' : '270px',
                minHeight: '150px',
                minWidth: '150px',
              }}
              onVisibleChange={(visible) => handleVisibility(visible, hit)}
              color={'#fff'}
            >
              <div>
                <Icon
                  size="small"
                  fill={tooltipVisible && tooltipVisible[hit.objectID] ? '#7965C6' : '#A793F6'}
                  style={{ cursor: 'pointer' }}
                  icon="Info"
                />
              </div>
            </Tooltip>
          </ContentRowRightWrapper>
        </ContentListItem>
      ))}
      <div ref={sentinelRef} aria-hidden />
    </SearchResultsWrapper>
  );
};
